/** Shared Mapbox GL helper for the admin app and the dispatcher. */

export const DEFAULT_CENTER = [-73.924, 40.754];
export const DEFAULT_ZOOM = 12;
export const POINT_ZOOM = 16;

const ACCURACY_SOURCE = 'signal-accuracy';
const STYLE = 'mapbox://styles/mapbox/standard';

let tokenPromise = null;
let glPromise = null;
let mapboxglRef = null;
const markers = new WeakMap();

function fetchToken() {
  if (!tokenPromise) {
    tokenPromise = (async () => {
      try {
        const response = await fetch('/api/public/map-config');
        if (!response.ok) return null;
        const body = await response.json();
        const token = String(body?.token || '').trim();
        return token || null;
      } catch {
        return null;
      }
    })();
  }
  return tokenPromise;
}

export function loadMapbox() {
  if (!glPromise) {
    glPromise = (async () => {
      const token = await fetchToken();
      if (!token) return null;
      const mod = await import('mapbox-gl');
      await import('mapbox-gl/dist/mapbox-gl.css');
      const mapboxgl = mod.default || mod;
      mapboxgl.accessToken = token;
      mapboxglRef = mapboxgl;
      return mapboxgl;
    })().catch((err) => {
      glPromise = null;
      throw err;
    });
  }
  return glPromise;
}

export async function createMap(container, { center = DEFAULT_CENTER, zoom = DEFAULT_ZOOM, scrollZoom = false } = {}) {
  const mapboxgl = await loadMapbox();
  if (!mapboxgl || !container) return null;
  const map = new mapboxgl.Map({
    container,
    style: STYLE,
    center,
    zoom,
    projection: 'globe',
    scrollZoom,
    dragRotate: false,
    pitchWithRotate: false,
    attributionControl: false,
  });
  map.addControl(new mapboxgl.AttributionControl({ compact: true }));
  map.touchZoomRotate.disableRotation();
  return map;
}

function circleFeature(lng, lat, radiusMeters) {
  const steps = 64;
  const earth = 6378137;
  const latRad = (lat * Math.PI) / 180;
  const lngRad = (lng * Math.PI) / 180;
  const angular = radiusMeters / earth;
  const coords = [];
  for (let i = 0; i <= steps; i += 1) {
    const bearing = (i / steps) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(latRad) * Math.cos(angular) + Math.cos(latRad) * Math.sin(angular) * Math.cos(bearing)
    );
    const lng2 =
      lngRad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angular) * Math.cos(latRad),
        Math.cos(angular) - Math.sin(latRad) * Math.sin(lat2)
      );
    coords.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [coords] },
    properties: {},
  };
}

function accuracyData(lat, lng, accuracyMeters) {
  const meters = Number(accuracyMeters);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(meters) || meters < 1) {
    return { type: 'FeatureCollection', features: [] };
  }
  return circleFeature(lng, lat, meters);
}

function drawAccuracy(map) {
  if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) return false;
  const data = map._signalAccuracy;
  if (!data) return false;
  try {
    const existing = map.getSource(ACCURACY_SOURCE);
    if (existing) {
      existing.setData(data);
      return true;
    }
    map.addSource(ACCURACY_SOURCE, { type: 'geojson', data });
    map.addLayer({
      id: `${ACCURACY_SOURCE}-fill`,
      type: 'fill',
      source: ACCURACY_SOURCE,
      slot: 'top',
      paint: { 'fill-color': '#1d4ed8', 'fill-opacity': 0.18 },
    });
    map.addLayer({
      id: `${ACCURACY_SOURCE}-line`,
      type: 'line',
      source: ACCURACY_SOURCE,
      slot: 'top',
      paint: { 'line-color': '#1d4ed8', 'line-width': 1.5, 'line-opacity': 0.7 },
    });
    return true;
  } catch (err) {
    if (!map._signalAccuracyWarned) {
      map._signalAccuracyWarned = true;
      console.warn('Could not draw the location accuracy circle.', err);
    }
    return false;
  }
}

function applyAccuracy(map, data) {
  map._signalAccuracy = data;
  if (!map._signalAccuracyRetry) {
    map._signalAccuracyRetry = () => {
      if (!drawAccuracy(map)) return;
      map.off('idle', map._signalAccuracyRetry);
    };
  }
  if (drawAccuracy(map)) {
    map.off('idle', map._signalAccuracyRetry);
    return;
  }
  map.off('idle', map._signalAccuracyRetry);
  map.on('idle', map._signalAccuracyRetry);
}

export function zoomForAccuracy(accuracyMeters) {
  const meters = Number(accuracyMeters);
  if (!Number.isFinite(meters) || meters < 80) return POINT_ZOOM;
  if (meters < 250) return 15;
  if (meters < 800) return 14;
  if (meters < 2000) return 13;
  return 12;
}

/**
 * Drop or move the pin. Resizes first, then recentres, so a map that was
 * hidden when it was created still frames the pin (the old Leaflet preview
 * recentred before the container had a size).
 */
export function showPoint(map, { lat, lng, accuracyMeters, draggable = false, onDrag, zoom, pan = true } = {}) {
  const mapboxgl = mapboxglRef;
  if (!mapboxgl || !map || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return null;
  const point = [Number(lng), Number(lat)];
  map.resize();
  let marker = markers.get(map);
  if (!marker) {
    marker = new mapboxgl.Marker({ color: '#1d4ed8', draggable: Boolean(draggable) }).setLngLat(point).addTo(map);
    marker.on('dragend', () => {
      const next = marker.getLngLat();
      if (typeof marker._signalOnDrag === 'function') marker._signalOnDrag(next.lat, next.lng);
    });
    markers.set(map, marker);
  } else {
    marker.setLngLat(point);
    marker.setDraggable(Boolean(draggable));
  }
  marker._signalOnDrag = typeof onDrag === 'function' ? onDrag : null;
  const targetZoom = zoom ?? (pan ? zoomForAccuracy(accuracyMeters) : map.getZoom());
  const frame = () => {
    map.resize();
    if (!pan) return;
    map.jumpTo({ center: point, zoom: targetZoom });
  };
  frame();
  requestAnimationFrame(frame);
  applyAccuracy(map, accuracyData(Number(lat), Number(lng), accuracyMeters));
  return marker;
}

export function clearPoint(map) {
  if (!map) return;
  const marker = markers.get(map);
  if (marker) {
    marker.remove();
    markers.delete(map);
  }
  applyAccuracy(map, accuracyData(null, null, null));
}

function featureLabel(properties) {
  const full = String(properties?.full_address || '').trim();
  if (full) return full;
  const name = String(properties?.name || '').trim();
  const place = String(properties?.place_formatted || '').trim();
  return [name, place].filter(Boolean).join(', ');
}

export async function searchAddress(query, { signal } = {}) {
  const token = await fetchToken();
  if (!token) {
    const error = new Error('Map is not configured.');
    error.code = 'unconfigured';
    throw error;
  }
  const q = String(query || '').trim();
  if (q.length < 3) return [];
  const url = new URL('https://api.mapbox.com/search/geocode/v6/forward');
  url.searchParams.set('q', q.slice(0, 200));
  url.searchParams.set('autocomplete', 'true');
  url.searchParams.set('country', 'us');
  url.searchParams.set('proximity', `${DEFAULT_CENTER[0]},${DEFAULT_CENTER[1]}`);
  url.searchParams.set('limit', '5');
  url.searchParams.set('language', 'en');
  url.searchParams.set('access_token', token);
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error('Address lookup failed.');
  const body = await response.json();
  const features = Array.isArray(body?.features) ? body.features : [];
  return features
    .map((feature) => {
      const coords = feature?.geometry?.coordinates;
      const lng = Number(feature?.properties?.coordinates?.longitude ?? coords?.[0]);
      const lat = Number(feature?.properties?.coordinates?.latitude ?? coords?.[1]);
      return { displayName: featureLabel(feature?.properties), lat, lng };
    })
    .filter((row) => row.displayName && Number.isFinite(row.lat) && Number.isFinite(row.lng));
}
